export interface FuzzyMatchItem {
  id: string;
  name: string;
}

/**
 * Studio vocabulary clusters, so "sr tap" finds "Varsity Tap" the way a real
 * person means it, not just how it is spelled. Bobbie's own example: senior,
 * sr and varsity are one word to her. Every search bar in the app runs
 * through fuzzyMatchScore, so the clusters live here once instead of being
 * re-taught on every page.
 *
 * Deliberately conservative: this is a same-meaning list, not a synonym
 * dictionary. Adding a loose cluster here loosens every search bar and the
 * Assistant's class/room matching at once.
 */
const ALIAS_CLUSTERS: string[][] = [
  ['sr', 'senior', 'varsity'],
  ['jr', 'junior'],
  ['rec', 'recreational'],
  ['comp', 'competition', 'competitive'],
  ['intro', 'beginner', 'beg'],
  ['int', 'intermediate'],
  ['adv', 'advanced'],
  ['mini', 'minis'],
  ['tech', 'technique'],
];

const ALIAS_LOOKUP: Map<string, string[]> = (() => {
  const map = new Map<string, string[]>();
  for (const cluster of ALIAS_CLUSTERS) {
    for (const word of cluster) map.set(word, cluster);
  }
  return map;
})();

/** A word's studio-vocabulary cluster, or just itself when it is not one. */
export function aliasClusterFor(word: string): string[] {
  return ALIAS_LOOKUP.get(word) ?? [word];
}

export interface FuzzyMatchResult<T extends FuzzyMatchItem> {
  item: T | null;
  candidates: T[];
  score: number;
  confidence: 'exact' | 'high' | 'low' | 'none';
}

const NUMBER_WORDS: Record<string, string> = {
  zero: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
};

function replaceNumberWords(value: string): string {
  return value.replace(
    /\b(zero|one|two|three|four|five|six|seven|eight|nine|ten|first|second)\b/g,
    (word) => {
      if (word === 'first') return '1';
      if (word === 'second') return '2';
      return NUMBER_WORDS[word] ?? word;
    }
  );
}

export function normalizeMatchText(value: string): string {
  return replaceNumberWords(value.toLowerCase())
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '');
}

export function normalizeMatchWords(value: string): string[] {
  const spaced = replaceNumberWords(value.toLowerCase())
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
  return spaced ? spaced.split(' ').filter((word) => word.length > 1) : [];
}

export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  const curr = Array.from({ length: b.length + 1 }, () => 0);
  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j += 1) prev[j] = curr[j];
  }
  return prev[b.length];
}

export function fuzzyMatchScore(rawValue: string, candidateName: string): number {
  const needle = normalizeMatchText(rawValue);
  const target = normalizeMatchText(candidateName);
  if (!needle || !target) return 0;
  if (needle === target) return 1;

  // Saying the leading words of a name IS the name, out loud: "Nora" for
  // Nora Gertrude, "Varsity Tap" for Varsity Tap Company. The plain
  // containment branch below scores a short first name against a long full
  // name too low to act on (Bobbie, 2026-08-07: the Assistant refused to
  // mark Nora absent because only "Nora Gertrude" was sure enough), so an
  // exact leading-words match gets its own tier above every caller's
  // autoMatchScore. Safe against wrong-pick by construction: two dancers
  // both named Nora both land at .95, the gap is zero, and findFuzzyMatch
  // below returns them as candidates to choose from instead of acting.
  const needleWords = normalizeMatchWords(rawValue);
  const targetWordList = normalizeMatchWords(candidateName);
  if (
    needleWords.length > 0 &&
    needleWords.length < targetWordList.length &&
    needleWords.every((word, index) => targetWordList[index] === word)
  ) {
    return 0.95;
  }

  if (target.includes(needle) || needle.includes(target)) {
    const shorter = Math.min(needle.length, target.length);
    const longer = Math.max(needle.length, target.length);
    return 0.82 + 0.14 * (shorter / longer);
  }

  const maxLen = Math.max(needle.length, target.length);
  const editScore = maxLen === 0 ? 0 : 1 - levenshtein(needle, target) / maxLen;
  const targetWords = new Set(targetWordList);

  // Each query word counts as matched if it is IN the target verbatim, or if
  // any word in its studio-vocabulary cluster is - "sr" matching a target
  // that says "varsity" is the whole point of ALIAS_CLUSTERS above. Exact
  // words and alias words are tallied separately so an alias-only match can
  // be scored lower than a real one.
  let exactShared = 0;
  let aliasShared = 0;
  for (const word of needleWords) {
    if (targetWords.has(word)) {
      exactShared += 1;
      continue;
    }
    const cluster = aliasClusterFor(word);
    if (cluster.length > 1 && cluster.some((alias) => alias !== word && targetWords.has(alias))) {
      aliasShared += 1;
    }
  }
  const matchedWords = exactShared + aliasShared;
  const tokenScore = needleWords.length === 0 ? 0 : matchedWords / needleWords.length;
  // An alias-only match is capped well below the exact-word ceiling (.9) and
  // below both auto-select thresholds every caller uses (highConfidenceScore
  // .86, autoMatchScore .94, see findFuzzyMatch below), on purpose: "sr tap"
  // has to be able to SURFACE Varsity Tap as an option, but it must never be
  // confident enough to book, message or otherwise act on it alone. A query
  // that also matches on real spelling (editScore, or an exact/prefix hit
  // above) is untouched by this cap - the alias layer only ever adds options,
  // it never takes away a match the raw string already earned.
  const tokenCeiling = aliasShared > 0 ? 0.8 : 0.9;
  return Math.max(editScore, tokenScore * tokenCeiling);
}

export function findFuzzyMatch<T extends FuzzyMatchItem>(
  rawValue: string,
  items: T[],
  options: {
    minimumScore?: number;
    highConfidenceScore?: number;
    autoMatchScore?: number;
    minimumGap?: number;
    maxCandidates?: number;
  } = {}
): FuzzyMatchResult<T> {
  const minimumScore = options.minimumScore ?? 0.72;
  const highConfidenceScore = options.highConfidenceScore ?? 0.86;
  const autoMatchScore = options.autoMatchScore ?? 0.94;
  const minimumGap = options.minimumGap ?? 0.08;
  const maxCandidates = options.maxCandidates ?? 5;
  const needle = normalizeMatchText(rawValue);
  if (needle.length < 2) {
    return { item: null, candidates: [], score: 0, confidence: 'none' };
  }

  const ranked = items
    .map((item) => ({ item, score: fuzzyMatchScore(rawValue, item.name) }))
    .filter((entry) => entry.score >= minimumScore)
    .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name));

  if (ranked.length === 0) {
    return { item: null, candidates: [], score: 0, confidence: 'none' };
  }

  const best = ranked[0];
  const runnerUp = ranked[1];
  const candidates = ranked.slice(0, maxCandidates).map((entry) => entry.item);
  const hasClearGap = !runnerUp || best.score - runnerUp.score >= minimumGap;
  if (best.score === 1 && (!runnerUp || runnerUp.score < 1)) {
    return { item: best.item, candidates: [], score: best.score, confidence: 'exact' };
  }
  if (best.score >= autoMatchScore && hasClearGap) {
    return { item: best.item, candidates: [], score: best.score, confidence: 'high' };
  }
  if (!runnerUp && best.score >= highConfidenceScore) {
    return { item: best.item, candidates: [], score: best.score, confidence: 'high' };
  }
  if (runnerUp && best.score >= highConfidenceScore && best.score - runnerUp.score >= minimumGap) {
    return { item: best.item, candidates: [], score: best.score, confidence: 'high' };
  }

  return { item: null, candidates, score: best.score, confidence: 'low' };
}

/**
 * Best fuzzy score for ONE query word against a set of TEXT FIELDS (a
 * title+body pair, a name+description, ...), tried both as a whole field
 * (cheap, and enough on its own for a short field) and against every
 * individual WORD inside each field.
 *
 * That second pass is the fix a whole-field-only comparison misses: a
 * typo'd query word compared to one target WORD keeps Levenshtein's
 * denominator small, so a one-letter slip ("rehersal") still reads as
 * close to "rehearsal". The identical slip compared to a whole multi-word
 * field does not - measured against a several-hundred-character string,
 * the same one-letter difference scores near zero every time. Matching a
 * plural against its singular ("Costumes" vs "costume") has the same
 * shape: fuzzyMatchScore's own containment shortcut handles it easily
 * word-to-word, but never fires against a whole field where the words are
 * not adjacent.
 *
 * Originally lived twice - once in Messages.tsx (search Past messages)
 * and, before this pass, missing entirely from bulletinMatchesSearch
 * (bulletins.ts), which only ever scored a whole field and so missed
 * exactly this class of misspelling against a longer announcement title.
 * One home now, so every caller scores text the same way.
 */
export function bestFieldWordScore(queryWord: string, fields: string[]): number {
  let best = 0;
  for (const field of fields) {
    const wholeFieldScore = fuzzyMatchScore(queryWord, field);
    if (wholeFieldScore > best) best = wholeFieldScore;
    for (const word of normalizeMatchWords(field)) {
      const wordScore = fuzzyMatchScore(queryWord, word);
      if (wordScore > best) best = wordScore;
    }
    if (best >= 1) return best;
  }
  return best;
}

/**
 * Whether EVERY word in `query` has its own best match somewhere across
 * `fields` (bestFieldWordScore above), each at least `threshold`. Every
 * multi-field, free-text search box in the app wants this exact shape:
 * adding a second query word can only narrow the results, it can never
 * break a match the first word already found on its own ("recital
 * costume" must not score below threshold just because "costume" alone
 * would have scored fine against a field literally spelled "Costumes").
 *
 * A blank query always matches (every caller's own pre-existing contract,
 * now enforced in one place instead of copied into every caller): an
 * empty search box shows everything, never nothing, regardless of whether
 * an item even has any usable fields. Fields that are missing/empty are
 * otherwise skipped, and an item with a real query but zero usable fields
 * matches nothing.
 */
export function fieldsMatchQuery(
  fields: ReadonlyArray<string | null | undefined>,
  rawQuery: string,
  threshold: number
): boolean {
  const query = rawQuery.trim();
  if (!query) return true;
  const usableFields = fields.filter((field): field is string => !!field);
  if (usableFields.length === 0) return false;
  const normalizedWords = normalizeMatchWords(query);
  const words = normalizedWords.length > 0 ? normalizedWords : [query.toLowerCase()];
  return words.every((word) => bestFieldWordScore(word, usableFields) >= threshold);
}

/**
 * How the fuzzy path treats the words of a query once the whole-string
 * substring path has said no. A word this short or shorter has to be
 * spelled right: one wrong letter in a three-letter name scores .667
 * (Ava/Eva, Kim/Tim, Ben/Ken, Zoe/Joe, Mia/Mya are all one edit apart),
 * so at any bar that forgives it every three-letter name would find every
 * other three-letter name one letter away from it, and the roster boxes
 * do not rank, so the one you meant is not first. Measured on a 30-name
 * roster in the refute round on #1160: "ava" returned Ava, Eva AND Ana,
 * "kim" returned Kim and Tim. Words of four letters or more forgive one
 * edit (.75), seven or more forgive two.
 */
export const LOOSE_MATCH_SHORT_WORD = 3;

/**
 * The per-word bar for looseMatch's fuzzy path. Above .667 on purpose, so
 * a three-letter word one letter off does NOT match (see above), and below
 * .75, so a four-letter word one letter off does ("nora" still finds a
 * Nora typed "nroa"). The containment tier starts at .82, so a query word
 * CONTAINED in a longer field word ("benn" in "bennett") always clears it.
 */
export const LOOSE_MATCH_THRESHOLD = 0.7;

/**
 * Edit distance that counts swapping two neighbouring letters as ONE edit
 * (optimal string alignment), where plain Levenshtein counts two. "calss"
 * for "class" and "nroa" for "nora" are the commonest typos a thumb makes,
 * and at LOOSE_MATCH_THRESHOLD a five-letter swap scored as two edits (.6)
 * would not forgive. Kept private to looseMatch: fuzzyMatchScore's own
 * Levenshtein stays as it is, because the Assistant's auto-match
 * thresholds were tuned against it.
 */
function swapAwareDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  const rows: number[][] = [];
  for (let i = 0; i <= a.length; i += 1) {
    rows.push(new Array<number>(b.length + 1).fill(0));
    rows[i][0] = i;
  }
  for (let j = 0; j <= b.length; j += 1) rows[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, rows[i - 2][j - 2] + 1);
      }
      rows[i][j] = best;
    }
  }
  return rows[a.length][b.length];
}

/**
 * Lower-cased with accents folded, so "renee" finds Renée and "zoe" finds
 * Zoë on every box that goes through looseMatch: the same fold the comp
 * board's own normalizeSearchText (compBoardDances.ts) does, and the fold
 * the Manage users dancer-link picker HAD through fuzzyMatchScore's
 * normalizeMatchText until it moved here (refute round two on #1160
 * found Zoë Park unreachable by any unaccented spelling).
 */
function foldSearchText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('en');
}

/** Folded, lower-cased text split into its words, one-letter words kept. */
function looseWords(text: string): string[] {
  return text
    .replace(/&/g, ' and ')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * How well ONE query word (four letters or more) matches the best word of
 * the fields: 1 when a field word is the same, .82 to .96 when one
 * contains the other (a query word typed with a letter too many, or a
 * plural), .8 when a studio alias stands in ("senior" for a field that
 * says "varsity"), otherwise 1 minus the swap-aware edit distance over
 * the longer length.
 *
 * Exported for lib/dancerMenuSearch.ts, which scores the words of a query
 * one at a time (so a level number or a weekday can be held to its own rule)
 * and needs the same per-word score looseMatchTier uses. Both arguments must
 * already be folded and lower-cased.
 */
export function looseWordScore(word: string, fieldWords: readonly string[]): number {
  let best = 0;
  const cluster = aliasClusterFor(word);
  for (const fieldWord of fieldWords) {
    if (fieldWord === word) return 1;
    let score: number;
    if (fieldWord.includes(word)) {
      // The query word inside a longer field word: a letter dropped from
      // the end, a singular typed against a plural.
      score = 0.82 + 0.14 * (word.length / fieldWord.length);
    } else if (cluster.length > 1 && cluster.includes(fieldWord)) {
      score = 0.8;
    } else if (fieldWord.length <= LOOSE_MATCH_SHORT_WORD) {
      // A short FIELD word is spelled right or it is not a match, the same
      // rule a short query word gets. Every title and every task body is
      // full of "for", "the", "and" and "one", each one edit from a real
      // four-letter query ("form", "thee", "four"), and a four-letter
      // query scored against a three-letter word divides by four, so one
      // edit (.75) clears the bar: on a ten-task list "form" went from one
      // hit to nine (refute round two on #1160). The price is that "kimm"
      // no longer finds Kim, which is the documented rule for three-letter
      // names anyway.
      score = 0;
    } else if (word.includes(fieldWord) && fieldWord.length / word.length >= 0.75) {
      // The query word swallowing a field word: an extra letter ("noraa"),
      // a plural typed against a singular field ("costumes"). Only when the
      // field word is most of the query word: "teacher" contains "each"
      // and "annabelle" contains "anna", and neither is a match.
      score = 0.82 + 0.14 * (fieldWord.length / word.length);
    } else {
      const longer = Math.max(word.length, fieldWord.length);
      score = 1 - swapAwareDistance(word, fieldWord) / longer;
    }
    if (score > best) best = score;
  }
  return best;
}

/**
 * How looseMatch matched, so a caller that has to ORDER its results (a
 * picker whose Enter key takes the first row, a list that truncates) can
 * put the rows the person literally typed above the rows a typo might
 * have meant: 3 = a field IS the query ("ann lee" typed, Ann Lee's own
 * name), 2 = the whole query is a substring of a field, 1 = every word
 * matched loosely, 0 = no match. looseMatch itself is `tier > 0`; a blank
 * query is tier 2 for every row, so a caller that sorts on the tier keeps
 * its own order.
 *
 * Refute round on #1160: with an unranked filter, typing "nora" in the
 * class form's teacher picker and pressing Enter assigned Cora Lane, the
 * alphabetically earlier fuzzy hit, to the class. Round two: with only a
 * substring tier, "ann lee" and Enter picked Ms. Joann Leeman, listed
 * before Ms. Ann Lee, which the hub-staging picker did too.
 */
export function looseMatchTier(
  fields: ReadonlyArray<string | null | undefined>,
  rawQuery: string,
  threshold = LOOSE_MATCH_THRESHOLD
): 0 | 1 | 2 | 3 {
  const typed = rawQuery.trim();
  if (!typed) return 2;
  // A query that FOLDS to nothing (a stray combining mark from a stuck dead
  // key) is not a blank query: it matches nothing rather than everything.
  const query = foldSearchText(typed).trim();
  if (!query) return 0;
  const haystacks = fields
    .filter((field): field is string => !!field)
    .map((field) => foldSearchText(field).trim());
  if (haystacks.some((haystack) => haystack === query)) return 3;
  if (haystacks.some((haystack) => haystack.includes(query))) return 2;
  if (query.length < 3) return 0;
  // An email address, a date, an id, a phone number, a class with a number
  // in its name: anything carrying a digit or an "@" is looked up, not
  // spelled, and matches exactly or not at all. Otherwise "2026-09-20"
  // splits into 2026 / 09 / 20 and finds every September date, and
  // "kim@example.com" finds Tim at the same domain (refute round on #1160,
  // findings 4 and 5).
  if (/[0-9@]/.test(query)) return 0;
  const words = looseWords(query);
  if (words.length === 0) return 0;
  const fieldWords = haystacks.flatMap(looseWords);
  const matched = words.every((word) => {
    if (word.length <= LOOSE_MATCH_SHORT_WORD) {
      // Short words are spelled right or they do not count. They still
      // count as the START of a longer word, so "ava b" still narrows to
      // the Bennetts and never to Eva Marsh, and a studio alias still
      // stands in for its long form ("sr tap" finds Varsity Tap).
      return aliasClusterFor(word).some((alias) => fieldWords.some((fieldWord) => fieldWord.startsWith(alias)));
    }
    return looseWordScore(word, fieldWords) >= threshold;
  });
  return matched ? 1 : 0;
}

/**
 * The one loose-match helper every plain substring search box in the app
 * routes through, so a typo forgives instead of finding nothing. Bobbie,
 * 2026-09-15: "Fuzzy matching please for searches." An audit found a long
 * tail of search boxes still doing exact `.includes`, one field or several
 * joined into one haystack; this is the single place that replaces all of
 * them, instead of re-teaching fuzzy scoring to each page.
 *
 * A blank query matches everything - the pre-existing contract of every box
 * this replaces, kept here so no caller has to guard it separately. The
 * query is trimmed and lower-cased with the same locale-aware call the rest
 * of the app's text handling uses (toLocaleLowerCase('en')).
 *
 * FIRST the substring path: if the query, as one whole string, is contained
 * in any usable field (lower-cased the same way), it is a match. That is
 * exactly what every box already did, so it keeps every exact hit working
 * today without any change in behaviour: an email address, an ISO date, an
 * id, a two-letter query, a phrase that spans two joined fields.
 *
 * THEN, only once the query is at least three characters and carries no
 * digit or "@", every word of the query has to be found on its own: a word
 * of four letters or more may be one letter off (looseWordScore at
 * LOOSE_MATCH_THRESHOLD, against every word of every field, a swap of two
 * neighbouring letters counting as one), a word of three or fewer has to
 * be spelled right and counts as the start of a field word. Below three characters the fuzzy path is skipped on purpose:
 * scoring a lone letter or two against every word of every field would
 * light up nearly everything in the list while a person is still typing a
 * short name - that exact leak was found and fixed on the Messages page
 * search first (searchQueryVariants's MIN_PAST_SEARCH_LENGTH guard, see
 * threadSearchNames.ts), and this helper carries the same guard everywhere
 * else that used to be plain substring.
 *
 * NOT for a long free-text body: the fuzzy path scores every query word
 * against every word of every field, which is fine for a name, a label or
 * a title and was measured at 200 to 340 ms per keystroke against the
 * How-To guide's full entry text (refute round on #1160). A row that
 * carries a body goes through looseMatchWithBody below instead.
 */
export function looseMatch(
  fields: ReadonlyArray<string | null | undefined>,
  rawQuery: string,
  threshold = LOOSE_MATCH_THRESHOLD
): boolean {
  return looseMatchTier(fields, rawQuery, threshold) > 0;
}

/**
 * looseMatch for a row that carries a prose BODY next to its short fields
 * (a task and its description, a feedback report and its message, a guide
 * entry and its answer and steps). The body is searched by plain substring
 * only: every whitespace-separated term of the query has to appear
 * somewhere in the short fields and body joined, the per-term pass the
 * How-To guide and the feedback board always had, so a date or a number
 * next to a word still finds its row ("calendar 2026-09-20"; refute round
 * two on #1160 found the feedback search had lost exactly that). Failing
 * that, the fuzzy path runs over the SHORT fields alone, never the body:
 * a body is full of three-letter words one edit from any four-letter
 * query, and scoring it costs 150 times the substring pass.
 *
 * NO SPECIAL "&"/"and" HANDLING HERE, ON PURPOSE (REFUTE_msg2_R4.md,
 * coordinator's round-five decision). D8 P3 tried three times to make a
 * body-wide `&` <-> "and" equivalence precise - a blanket substring
 * replace (round two, matched 202/209 real guide entries on a lone "&"),
 * a whole-word check (round three, still matched 200/209), and a
 * multi-token guard with "&" split into its own term (round four, matched
 * 200/209 again the moment any second token appeared at all, including a
 * doubled "&" or a single throwaway letter, and separately broke a
 * previously-narrow compound search like "Q&A" into three independent
 * checks that matched unrelated notes). Each attempt reopened the
 * near-everything match from a new side. Dropped rather than tried a
 * fourth time: this function is back to exactly what it was before any of
 * that work, `&` stays inside a glued token ("Q&A" is one token again,
 * a narrow literal substring check), and the pre-existing `&` -> "and"
 * expansion `looseWords` already applies to the FIELDS-only fuzzy
 * fallback below (`looseMatchTier`) is untouched and was never part of
 * this equivalence - see `tests/loose-match.test.mjs` for the pinned,
 * measured behaviour this leaves in place. If Bobbie ever wants a real
 * `&`/"and" equivalence in a body, it needs a real tokenizer with word
 * boundaries on both sides and a query-length floor, as its own packet -
 * not another pass at this same function.
 */
export function looseMatchWithBody(
  fields: ReadonlyArray<string | null | undefined>,
  body: ReadonlyArray<string | null | undefined>,
  rawQuery: string,
  threshold = LOOSE_MATCH_THRESHOLD
): boolean {
  const typed = rawQuery.trim();
  if (!typed) return true;
  const query = foldSearchText(typed).trim();
  if (!query) return false;
  const joined = [...fields, ...body]
    .filter((field): field is string => !!field)
    .map(foldSearchText)
    .join(' ');
  const terms = query.split(/\s+/).filter(Boolean);
  if (terms.every((term) => joined.includes(term))) return true;
  return looseMatchTier(fields, rawQuery, threshold) > 0;
}
